
import {
  defineComponent, computed, ref, onBeforeMount, watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useDevice } from 'next-vue-device-detector';
import { useMainStore } from '@/store/mainStore';
import AppLayout from '@/layouts/AppLayout.vue';
import AppSplashLayout from '@/layouts/AppSplashLayout.vue';
import AppCreateFolderPopUp from '@/components/AppCreateFolderPopUp.vue';
import AppQuotaWarning from '@/components/AppQuotaWarning.vue';
import AppFooter from '@/components/AppFooter.vue';
// import AppMenuPopUp from '@/components/AppMenuPopUp.vue';
import AppBottomPopUpSwitcher from '@/components/BottomPopUp/AppBottomPopUpSwitcher.vue';
import { ACCOUNT_STATUS } from '@/types/enums';

export default defineComponent({
  components: {
    AppBottomPopUpSwitcher,
    AppLayout,
    AppSplashLayout,
    AppCreateFolderPopUp,
    AppQuotaWarning,
    AppFooter,
  },
  setup() {
    const mainStore = useMainStore();

    const d = useDevice();

    const route = useRoute();
    const router = useRouter();
    const hideFooter = computed(() => route.meta?.hideFooter || !mainStore.user.accountId || !mainStore.user.accountStatuses.includes(ACCOUNT_STATUS.PHONE_NUMBER_CONFIRMED));
    const manualScroll = computed(() => route.meta?.manualScroll);

    const currentPage = computed(() => (route.meta.navigationPage));

    const showMenu = ref(false);
    const openKeyboard = ref(false);
    const isUserLoggedIn = computed(() => mainStore.user?.status === 1);

    const quotaWarningText = computed(() => {
      if (mainStore.softQuotaWarning) {
        return `Your account type only supports a total of ${mainStore.subscriptionQuota.filesQuota.maxSoft} items.
         You currently have ${mainStore.subscriptionQuota.filesQuota.current} items.
         We will allow you to upload up to ${mainStore.subscriptionQuota.filesQuota.maxHard} items, But please consider payment.`;
      }
      if (mainStore.hardQuotaWarning) {
        return `Your account type only supports a total of ${mainStore.subscriptionQuota.filesQuota.maxHard} items.
        You currently have ${mainStore.subscriptionQuota.filesQuota.current} items.`;
      }
      return '';
    });

    function closeQuotaWarning() {
      mainStore.hardQuotaWarning = false;
      mainStore.softQuotaWarning = false;
    }

    function handleOpenMenu() {
      if (isUserLoggedIn.value) {
        showMenu.value = true;
      } else {
        router.push('/login');
      }
    }

    const isResponsive = () => {
      if (route.name === 'DriveCasePublicView') {
        return false;
      }
      return true;
    };

    const isMobile = computed(() => (d.mobile || d.ipad) && isResponsive());
    const isAndroid = computed(() => d.android && isResponsive());

    watch(() => route.name, () => {
      mainStore.isMobile = isMobile.value && isResponsive();
      mainStore.isAndroid = isAndroid.value && isResponsive();
      mainStore.isResponsive = isResponsive();
    });
    watch(() => mainStore.isMobile, () => { mainStore.isMobile = isMobile.value && isResponsive(); });
    watch(() => mainStore.isAndroid, () => { mainStore.isAndroid = isAndroid.value && isResponsive(); });

    // We listen to the resize event (https://css-tricks.com/the-trick-to-viewport-units-on-mobile/)
    const body = document.querySelector('body');
    const viewport = window.visualViewport;
    let lastVH: number;
    let lastVW: number;
    const setVH = () => {
      const vh = viewport.height * 0.01;
      const vw = viewport.width * 0.01;
      if (lastVH === vh) {
        return;
      }

      if (body && isMobile.value && lastVW === vw) {
        if (lastVH > vh + 0.5) {
          openKeyboard.value = true;
          body.classList.add('open-keyboard');
        }
        if (lastVH + 0.5 < vh && document.querySelector('body.open-keyboard')) {
          openKeyboard.value = false;
          body.classList.remove('open-keyboard');
        }
      }

      lastVH = vh;
      lastVW = vw;

      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    window.addEventListener('scroll', () => {
      window.scrollTo(0, 0);
    });

    setVH();
    viewport.addEventListener('resize', setVH);

    onBeforeMount(() => {
      mainStore.isMobile = d.mobile;
      mainStore.isAndroid = d.android;
    });

    return {
      currentPage,
      showMenu,
      isUserLoggedIn,
      manualScroll,
      hideFooter,
      mainStore,
      handleOpenMenu,
      openKeyboard,
      quotaWarningText,
      closeQuotaWarning,
      isAndroid,
    };
  },
});
